import * as DocumentPicker from 'expo-document-picker';
import { FileSpreadsheet, Upload, X } from 'lucide-react-native';
import {
  type ChangeEvent,
  type ChangeEventHandler,
  type CSSProperties,
  type DragEvent,
  type RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from '../contexts/TranslationContext';
import { colors } from '../theme/colors';
import { tokens } from '../theme/tokens';
import { HStack } from './primitives';
import { Button } from './primitives/Button';
import { Text } from './primitives/Text';

const ACCEPTED_TYPES = [
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

const ACCEPTED_EXTENSIONS = ['.csv', '.xls', '.xlsx'];

const isAcceptedFile = (file: File): boolean =>
  ACCEPTED_TYPES.includes(file.type) ||
  ACCEPTED_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension));

interface FileUploadZoneProps {
  onFileSelected: (file: File | DocumentPicker.DocumentPickerAsset) => void;
  selectedFile: { name: string; size?: number } | null;
  onClear: () => void;
  disabled?: boolean;
}

type WebInputRef = RefObject<HTMLInputElement | null>;

export function FileUploadZone({
  onFileSelected,
  selectedFile,
  onClear,
  disabled,
}: Readonly<FileUploadZoneProps>) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [hasRejectedFile, setHasRejectedFile] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleNativePick = useCallback(async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: ACCEPTED_TYPES,
      copyToCacheDirectory: true,
    });

    if (!result.canceled && result.assets[0]) {
      onFileSelected(result.assets[0]);
    }
  }, [onFileSelected]);

  const handleWebFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) onFileSelected(file);
    },
    [onFileSelected],
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
      const file = event.dataTransfer.files[0];
      if (!file) return;
      if (isAcceptedFile(file)) {
        setHasRejectedFile(false);
        onFileSelected(file);
      } else {
        setHasRejectedFile(true);
      }
    },
    [onFileSelected],
  );

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  if (selectedFile) {
    return (
      <View style={styles.selectedContainer}>
        <HStack spacing="md" align="center" style={styles.selectedContent}>
          <FileSpreadsheet size={24} color={colors.status.success.main} />
          <View style={styles.fileInfo}>
            <Text variant="body" color={colors.text.primary} numberOfLines={1}>
              {selectedFile.name}
            </Text>
            {selectedFile.size ? (
              <Text variant="caption" color={colors.text.tertiary}>
                {(selectedFile.size / 1024).toFixed(1)} KB
              </Text>
            ) : null}
          </View>
          <Pressable onPress={onClear} hitSlop={8} testID="clear_file_button">
            <X size={18} color={colors.text.tertiary} />
          </Pressable>
        </HStack>
      </View>
    );
  }

  if (Platform.OS === 'web') {
    return (
      <View>
        <button
          type="button"
          data-testid="file_upload_zone"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{
            ...webStyles.dropzone,
            ...(isDragging ? webStyles.dropzoneActive : {}),
          }}
        >
          <Upload size={32} color={colors.primary[500]} />
          <Text variant="body" color={colors.text.primary} style={styles.dropzoneText}>
            {t('leadImport.fileUpload.dragAndDrop')}
          </Text>
          <Text variant="caption" color={colors.text.tertiary}>
            {t('leadImport.fileUpload.fileTypes')}
          </Text>
          <input
            ref={inputRef as unknown as WebInputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={handleWebFileChange as unknown as ChangeEventHandler<HTMLInputElement>}
            style={{ display: 'none' }}
          />
        </button>
        {hasRejectedFile ? (
          <Text
            testID="file_upload_zone_error"
            variant="caption"
            color={colors.status.error.main}
            accessibilityRole="alert"
            style={styles.errorText}
          >
            {t('leadImport.fileUpload.unsupportedType')}
          </Text>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.nativeContainer}>
      <Upload size={32} color={colors.primary[500]} />
      <Text variant="body" color={colors.text.tertiary} style={styles.nativeText}>
        {t('leadImport.fileUpload.fileTypes')}
      </Text>
      <Button
        testID="file_pick_button"
        variant="outline"
        size="sm"
        onPress={handleNativePick}
        disabled={disabled}
      >
        <Text variant="caption" color={colors.primary[500]}>
          {t('leadImport.fileUpload.chooseFile')}
        </Text>
      </Button>
    </View>
  );
}

const webStyles = {
  dropzone: {
    border: `2px dashed ${colors.border.muted}`,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.xl,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.md,
    backgroundColor: colors.background.secondary,
    minHeight: 160,
    cursor: 'pointer',
  } as CSSProperties,
  dropzoneActive: {
    borderColor: colors.primary[500],
    backgroundColor: colors.primary.muted,
  } as CSSProperties,
};

const styles = StyleSheet.create({
  errorText: {
    marginTop: tokens.spacing.sm,
  },
  dropzone: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: colors.border.muted,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.sm,
    backgroundColor: colors.background.secondary,
    minHeight: 160,
  },
  dropzoneActive: {
    borderColor: colors.primary[500],
    backgroundColor: colors.primary.muted,
  },
  dropzoneText: {
    marginTop: tokens.spacing.sm,
  },
  nativeContainer: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: colors.border.muted,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.md,
    backgroundColor: colors.background.secondary,
    minHeight: 160,
  },
  nativeText: {
    textAlign: 'center',
  },
  selectedContainer: {
    borderWidth: 1,
    borderColor: colors.status.success.main,
    borderRadius: tokens.radius.lg,
    backgroundColor: colors.status.success.bg,
    padding: tokens.spacing.lg,
  },
  selectedContent: {
    flex: 1,
  },
  fileInfo: {
    flex: 1,
  },
});
